.button {
    transition: var(--transition-fast);

    display: inline-flex;
    justify-content: center;
    align-items: center;

    font-size: 1rem;
    font-family: inherit;

    min-width: fit-content;
    border-style: solid;
    box-shadow: var(--dimension-1);
    box-sizing: border-box;
    padding-block-start: 0;
    padding-block-end: 0;

    white-space: nowrap;

    text-decoration: none;
    cursor: default;
}

.button:disabled,
.button[data-disabled],
.button[data-pending],
.button[aria-disabled],
.button.disabled {
    opacity: 0.5;
    box-shadow: none;
}

.button:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover {
    box-shadow: var(--dimension-2);
}

.large {
    height: var(--block-large);
    border-radius: var(--radius-loose);
    border-width: var(--border-thick);
}

.medium {
    height: var(--block-medium);
    border-radius: var(--radius-medium);
    border-width: var(--border-medium);
}

.small {
    height: var(--block-small);
    border-radius: var(--radius-tight);
    border-width: var(--border-thin);
}

.largePadding {
    padding-inline-start: var(--padding-large);
    padding-inline-end: var(--padding-large);
}

.mediumPadding {
    padding-inline-start: var(--padding-medium);
    padding-inline-end: var(--padding-medium);
}

.smallPadding {
    padding-inline-start: var(--padding-small);
    padding-inline-end: var(--padding-small);
}

.noPadding {
    padding-inline-start: 0;
    padding-inline-end: 0;
}

.bg {
    border-color: var(--color-bg1);
    background-color: transparent;
    color: var(--color-bg1);
}
.bg.filled {
    background-color: var(--color-bg1);
    color: var(--color-a0);
}
.bg:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.bg:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-bg0);
    background-color: var(--color-bg0);
    color: var(--color-a1);
}

.fg {
    border-color: var(--color-fg2);
    background-color: transparent;
    color: var(--color-fg2);
}
.fg.filled {
    background-color: var(--color-fg2);
    color: var(--color-bg2);
}
.fg:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.fg:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-fg3);
    background-color: var(--color-fg3);
    color: var(--color-bg0);
}

.accentPrimary {
    border-color: var(--color-a0);
    background-color: transparent;
    color: var(--color-a0);
}
.accentPrimary.filled {
    background-color: var(--color-a0);
    color: var(--color-bg1);
}
.accentPrimary:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.accentPrimary:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-a1);
    background-color: var(--color-a1);
    color: var(--color-bg0);
}

.accentSecondary {
    border-color: var(--color-b0);
    background-color: transparent;
    color: var(--color-a0);
}
.accentSecondary.filled {
    background-color: var(--color-b0);
}
.accentSecondary:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.accentSecondary:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-b1);
    background-color: var(--color-b1);
    color: var(--color-a1);
}

.accentTertiary {
    border-color: var(--color-c0);
    background-color: transparent;
    color: var(--color-a0);
}
.accentTertiary.filled {
    background-color: var(--color-c0);
}
.accentTertiary:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.accentTertiary:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-c1);
    background-color: var(--color-c1);
    color: var(--color-a1);
}

.red {
    border-color: var(--color-red);
    background-color: transparent;
    color: var(--color-dark-red);
}
.red.filled {
    background-color: var(--color-red);
    color: var(--color-bg1);
}
.red:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.red:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible {
    border-color: var(--color-dark-red);
    background-color: var(--color-dark-red);
    color: var(--color-bg0);
}

.light,
.dark {
    border-color: transparent;
    background-color: transparent;
    box-shadow: none;
    color: inherit;
}
a.light {
    color: var(--color-bg1);
}
a.dark {
    color: var(--color-fg1);
}
.light:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.light:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible,
.light.filled {
    background-color: var(--tint-light);
    box-shadow: none;
}
.dark:not(:disabled):not([data-disabled]):not([data-pending]):not([aria-disabled]):not(.disabled):hover,
.dark:not(:disabled):not([data-disabled]):not([aria-disabled]):not(.disabled):focus-visible,
.dark.filled {
    background-color: var(--tint-dark);
    box-shadow: none;
}

.tooltip {
    box-shadow: var(--dimension-1);
    border-radius: var(--radius-tight);
    background: var(--color-bg1);
    color: var(--color-fg0);
    forced-color-adjust: none;
    outline: none;
    padding: var(--padding-small) var(--padding-medium);
    max-width: 150px;
    font-size: 0.875rem;
    /* fixes FF gap */
    transform: translate3d(0, 0, 0);
    transition:
        transform 200ms,
        opacity 200ms;
}

.tooltip[data-entering],
.tooltip[data-exiting] {
    transform: var(--origin);
    opacity: 0;
}

.tooltip[data-placement='top'] {
    margin-bottom: var(--padding-medium);
    --origin: translateY(4px);
}

.tooltip[data-placement='top'] .overlayArrow svg {
    filter: drop-shadow(0 6px 2px rgba(0, 0, 0, 0.25));
}

.tooltip[data-placement='bottom'] {
    margin-top: var(--padding-medium);
    --origin: translateY(-4px);
}

.tooltip[data-placement='bottom'] .overlayArrow svg {
    filter: drop-shadow(0 6px 4px rgba(0, 0, 0, 0.15));
    transform: rotate(180deg);
}

.tooltip[data-placement='right'] {
    margin-left: var(--padding-medium);
    --origin: translateX(-4px);
}

.tooltip[data-placement='right'] .overlayArrow svg {
    filter: drop-shadow(4px 2px 2px rgba(0, 0, 0, 0.25));
    transform: rotate(90deg);
}

.tooltip[data-placement='left'] {
    margin-right: var(--padding-medium);
    --origin: translateX(4px);
}

.tooltip[data-placement='left'] .overlayArrow svg {
    filter: drop-shadow(-4px 2px 2px rgba(0, 0, 0, 0.25));
    transform: rotate(-90deg);
}

.overlayArrow svg {
    display: block;
    fill: var(--color-bg1);
}
